import React, { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: 'buyer' | 'seller' | 'professional';
  phone: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

interface UserRole {
  id: string;
  user_id: string;
  role: 'buyer' | 'seller' | 'professional';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userRoles: UserRole[];
  activeRole: string | null;
  loading: boolean;
  signUp: (email: string, password: string, userData?: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  switchRole: (role: string) => Promise<boolean>;
  addRole: (role: 'buyer' | 'seller' | 'professional') => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      return data as Profile;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  const fetchUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching user roles:', error);
        return [];
      }

      return data as UserRole[];
    } catch (error) {
      console.error('Error fetching user roles:', error);
      return [];
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer profile and roles fetching to avoid potential deadlock
          setTimeout(async () => {
            const [userProfile, roles] = await Promise.all([
              fetchProfile(session.user.id),
              fetchUserRoles(session.user.id)
            ]);
            
            setProfile(userProfile);
            setUserRoles(roles);
            
            // Set active role based on user_roles table
            const activeUserRole = roles.find(r => r.is_active);
            if (activeUserRole) {
              setActiveRole(activeUserRole.role);
              // Update profile role to match active role
              if (userProfile && userProfile.role !== activeUserRole.role) {
                setProfile({ ...userProfile, role: activeUserRole.role as any });
              }
            } else if (userProfile) {
              setActiveRole(userProfile.role);
            }
            
            setLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setUserRoles([]);
          setActiveRole(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        const [userProfile, roles] = await Promise.all([
          fetchProfile(session.user.id),
          fetchUserRoles(session.user.id)
        ]);
        
        setProfile(userProfile);
        setUserRoles(roles);
        
        // Set active role based on user_roles table
        const activeUserRole = roles.find(r => r.is_active);
        if (activeUserRole) {
          setActiveRole(activeUserRole.role);
          // Update profile role to match active role
          if (userProfile && userProfile.role !== activeUserRole.role) {
            setProfile({ ...userProfile, role: activeUserRole.role as any });
          }
        } else if (userProfile) {
          setActiveRole(userProfile.role);
        }
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, userData?: any) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: userData
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const switchRole = async (role: string) => {
    if (!user) return false;
    
    try {
      const { data, error } = await supabase.rpc('switch_user_role', {
        user_uuid: user.id,
        new_role: role
      });

      if (error) throw error;
      if (!data) return false;

      // Update local state
      const updatedRoles = userRoles.map(r => ({
        ...r,
        is_active: r.role === role
      }));
      setUserRoles(updatedRoles);
      setActiveRole(role);
      
      // Update profile role
      if (profile) {
        setProfile({ ...profile, role: role as any });
      }

      return true;
    } catch (error) {
      console.error('Error switching role:', error);
      return false;
    }
  };

  const addRole = async (role: 'buyer' | 'seller' | 'professional') => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: user.id,
          role: role,
          is_active: false
        });

      if (error) throw error;

      // Refresh user roles
      const roles = await fetchUserRoles(user.id);
      setUserRoles(roles);

      return true;
    } catch (error) {
      console.error('Error adding role:', error);
      return false;
    }
  };

  const signOut = async () => {
    try {
      // Always clear local state first
      setUser(null);
      setSession(null);
      setProfile(null);
      setUserRoles([]);
      setActiveRole(null);
      
      // Try to sign out from Supabase, but don't fail if session doesn't exist
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // Ignore errors - we've already cleared local state
      console.log('Sign out completed (local state cleared)');
    }
    
    // Force redirect to home page
    window.location.href = '/';
  };

  const value = {
    user,
    session,
    profile,
    userRoles,
    activeRole,
    loading,
    signUp,
    signIn,
    signOut,
    switchRole,
    addRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};